import React, { useEffect, useState } from "react";
import { Spin, Result, Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useNotification } from "../../store/context/notification-context";
import { setAdminLogin, updateUserData } from "../../store/redux/actions/userActions";
import { usePermissions } from "../../store/context/permission-context";
import { useApiRequests } from "../../utils/api/https/executor";

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { triggerNotification } = useNotification();
  const { updatePermissions } = usePermissions();
  const { post } = useApiRequests();

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Get OAuth parameters from URL
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");
      const provider = searchParams.get("provider");

      // Check for OAuth errors
      if (error) {
        throw new Error(error);
      }

      if (!code) {
        throw new Error("No authorization code received");
      }

      // Get stored auth context
      const returnUrl = sessionStorage.getItem("auth_return_url") || "/dashboard";
      const isAdmin = sessionStorage.getItem("auth_is_admin") === "true";

      // Exchange code for tokens and user data
      const response = await post("/api/auth/oauth/callback", {
        code,
        state,
        provider,
        isAdmin,
      });

      const result = response?.outData;

      if (result?.isSuccessful) {
        // Store user session
        if (isAdmin) {
          await dispatch(setAdminLogin(result.data));
        } else {
          await dispatch(updateUserData(result.data));
        }
        
        updatePermissions();

        // Clear session storage
        sessionStorage.removeItem("auth_return_url");
        sessionStorage.removeItem("auth_is_admin");

        setStatus("success");

        // Redirect after brief success message
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 1500);

        triggerNotification({
          type: "success",
          message: "Login Successful",
          description: `Welcome back! You've been authenticated via ${provider}.`,
        });
      } else {
        throw new Error(result?.message || "Authentication failed");
      }
    } catch (error) {
      console.error("OAuth callback error:", error);
      setStatus("error");
      setErrorMessage(error.message || "Authentication failed");
      
      triggerNotification({
        type: "error",
        message: "Authentication Failed",
        description: error.message || "An error occurred during authentication.",
      });
    }
  };

  const handleRetry = () => {
    const isAdmin = sessionStorage.getItem("auth_is_admin") === "true";
    navigate(isAdmin ? "/admin/sso-login" : "/sso-login", { replace: true });
  };

  const containerStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
    padding: "20px",
  };

  if (status === "loading") {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: "center" }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            size="large"
          />
          <div style={{ marginTop: 24, fontSize: 16, color: "#595959" }}>
            Completing authentication...
          </div>
          <div style={{ marginTop: 8, fontSize: 14, color: "#8c8c8c" }}>
            Please wait while we verify your credentials
          </div>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div style={containerStyle}>
        <Result
          status="success"
          title="Authentication Successful!"
          subTitle="Redirecting you to the application..."
        />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={containerStyle}>
        <Result
          status="error"
          title="Authentication Failed"
          subTitle={errorMessage}
          extra={[
            <Button type="primary" key="retry" onClick={handleRetry}>
              Try Again
            </Button>,
          ]}
        />
      </div>
    );
  }

  return null;
};

export default OAuthCallback;
